/* assets/littleg-upload-child-binding.js — FAMILY-UPLOAD-CHILD-CONTRACT-05
 * Owner: optional Upload-to-Child Profile binding, task-context projection and confirm payload snapshot.
 * Parent value: identify whose notice it is before packing, preparation and guardian confirmation.
 * Profile persistence/API owner remains assets/littleg-family-account.js.
 * Confirm transport is augmented only through LittleGUploadTransport.registerConfirmContributor().
 * Boundary: no Child Profile CRUD, direct profile storage key, Upload U01–U06 state ownership or new DOM containers.
 */
(function littleGUploadChildBinding(window, document) {
  'use strict';

  var VERSION = '2026-07-29-upload-child-05';
  var HOST_SELECTOR = '[data-upload-child-binding]';
  var ROOT_SELECTOR = '[data-littleg-upload-workspace]';
  var activeHost = null;
  var HEADING_LABELS = Object.freeze({
    '[data-upload-packing-heading]': '今日要帶咩',
    '[data-upload-prep-heading]': '今日清單',
    '[data-upload-confirm-heading]': '最後確認',
  });

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function profilesApi() {
    return window.LittleGChildProfiles || null;
  }
  function familyData() {
    return window.LittleGFamilyData || null;
  }
  function listProfiles() {
    var api = profilesApi();
    return api && typeof api.list === 'function' ? api.list() : [];
  }
  function activeProfile() {
    var api = profilesApi();
    return api && typeof api.getActive === 'function' ? api.getActive() : null;
  }
  function selectedProfile(host) {
    var select = host && host.querySelector('[data-upload-child-picker]');
    var id = clean(select && select.value);
    return (
      listProfiles().find(function (profile) {
        return clean(profile.id) === id;
      }) || null
    );
  }
  function bindingSnapshot(host) {
    var profile = selectedProfile(host);
    return profile
      ? {
          profile_id: clean(profile.id),
          nickname_snapshot: clean(profile.nickname),
          storage_mode: 'local_private',
        }
      : {
          profile_id: '',
          nickname_snapshot: '未指定孩子',
          storage_mode: 'local_private',
        };
  }
  function activityDate(root) {
    var row = root && root.querySelector('[data-notice-field][data-field-key="activity_date"]');
    var input = row && row.querySelector('[data-field-value]');
    return clean(input && input.value);
  }
  function projectTaskContext(host) {
    var root = host && (host.closest(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR));
    if (!(root instanceof HTMLElement)) return;
    var binding = bindingSnapshot(host);
    var date = activityDate(root);
    var parts = [binding.nickname_snapshot];
    if (date) parts.push(date);
    root.setAttribute('data-upload-task-context', parts.join('|'));
    root.setAttribute(
      'data-upload-task-context-state',
      binding.profile_id ? 'identified' : 'unassigned'
    );
    Object.keys(HEADING_LABELS).forEach(function (selector) {
      var heading = root.querySelector(selector);
      if (!heading) return;
      heading.setAttribute('data-task-context-label', parts.join(' · '));
      heading.textContent =
        selector === '[data-upload-prep-heading]'
          ? HEADING_LABELS[selector]
          : parts.concat(HEADING_LABELS[selector]).join('｜');
    });
  }
  function syncRoot(host) {
    var root = host && (host.closest(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR));
    var binding = bindingSnapshot(host);
    if (root) {
      root.setAttribute('data-upload-child-profile-id', binding.profile_id);
      root.setAttribute('data-upload-child-label', binding.nickname_snapshot);
      root.setAttribute(
        'data-upload-child-binding-state',
        binding.profile_id ? 'bound' : 'unassigned'
      );
    }
    var status = host.querySelector('[data-upload-child-status]');
    if (status) {
      status.textContent = binding.profile_id
        ? '已指定給「' + binding.nickname_snapshot + '」。'
        : '未指定孩子。';
    }
    var owner = familyData();
    if (
      owner &&
      typeof owner.getNoticeDraft === 'function' &&
      typeof owner.saveNoticeDraft === 'function'
    ) {
      var draft = owner.getNoticeDraft();
      if (draft) owner.saveNoticeDraft(Object.assign({}, draft, { child: binding }));
    }
    projectTaskContext(host);
    return binding;
  }
  function render(host) {
    var select = host.querySelector('[data-upload-child-picker]');
    if (!select) return;
    var profiles = listProfiles();
    host.hidden = profiles.length === 0;
    var previous = clean(select.value);
    var draftOwner = familyData();
    var draft =
      draftOwner && typeof draftOwner.getNoticeDraft === 'function'
        ? draftOwner.getNoticeDraft()
        : null;
    var preferred =
      previous ||
      clean(draft && draft.child && draft.child.profile_id) ||
      clean(activeProfile() && activeProfile().id);
    select.replaceChildren();
    var empty = document.createElement('option');
    empty.value = '';
    empty.textContent = '未指定孩子';
    select.appendChild(empty);
    profiles.forEach(function (profile) {
      var option = document.createElement('option');
      option.value = clean(profile.id);
      option.textContent = clean(profile.nickname);
      select.appendChild(option);
    });
    select.disabled = profiles.length === 0;
    select.value = profiles.some(function (profile) {
      return clean(profile.id) === preferred;
    })
      ? preferred
      : '';
    syncRoot(host);
  }
  function registerContributor(host) {
    var transport = window.LittleGUploadTransport;
    if (!transport || typeof transport.registerConfirmContributor !== 'function') return;
    transport.registerConfirmContributor(
      'child-binding',
      function (payload) {
        var next = Object.assign({}, payload || {});
        next.notice = Object.assign({}, next.notice || {}, { child: bindingSnapshot(host) });
        return next;
      },
      20
    );
  }
  function bind(host) {
    if (!host || host.getAttribute('data-upload-child-bound') === VERSION) return;
    host.setAttribute('data-upload-child-bound', VERSION);
    activeHost = host;
    render(host);
    registerContributor(host);
    var root = host.closest(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR);
    var select = host.querySelector('[data-upload-child-picker]');
    if (select)
      select.addEventListener('change', function () {
        var binding = syncRoot(host);
        document.dispatchEvent(
          new CustomEvent('littleg:upload-child-change', {
            bubbles: true,
            detail: { child: binding },
          })
        );
      });
    if (root) {
      root.addEventListener('input', function (event) {
        if (
          event.target &&
          event.target.closest('[data-notice-field][data-field-key="activity_date"]')
        )
          projectTaskContext(host);
      });
      root.addEventListener('littleg:upload-state-change', function () {
        window.setTimeout(function () {
          projectTaskContext(host);
        }, 0);
      });
    }
    document.addEventListener('littleg:child-profile-change', function () {
      render(host);
    });
  }
  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(HOST_SELECTOR), bind);
  }

  window.LittleGUploadChildBinding = Object.freeze({
    version: VERSION,
    getSnapshot: function () {
      return bindingSnapshot(activeHost);
    },
    projectTaskContext: function () {
      if (activeHost) projectTaskContext(activeHost);
    },
  });

  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  else boot(document);
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})(window, document);
